<template>
  <div
    class="bill-sale-product-search"
    :class="{ 'bill-sale-product-search--compact': compact }"
  >
    <b-row
      class="align-items-center no-gutters"
      :class="{ 'flex-column': compact }"
    >
      <b-col :cols="compact ? 12 : 4" :class="compact ? 'mb-2 px-0' : 'pr-1'">
        <b-form-select
          class="select-style"
          v-model="searchType"
          :options="searchTypeOptions"
          size="sm"
          value-field="id"
          text-field="name"
          :disabled="disabled"
        ></b-form-select>
      </b-col>
      <b-col :cols="compact ? 12 : 8" :class="compact ? 'px-0' : 'pl-1'">
        <treeselect
          :key="`${storeId}-${searchType}`"
          :load-options="loadOptionProductsDebounced"
          :multiple="false"
          :async="true"
          :placeholder="placeHolder"
          :cache-options="false"
          retryText="Thử lại..."
          retryTitle="Nhấp thử lại"
          searchPromptText="Nhập để tìm kiếm..."
          loadingText="Đang tải..."
          noResultsText="Không có kết quả"
          openDirection="bottom"
          append-to-body
          :wrapper-class="portalWrapperClass"
          :disabled="disabled || !storeId"
          @select="onSelect"
        >
          <label slot="option-label" slot-scope="{ node, labelClassName }" :class="labelClassName">
            <div class="search-option-item">
              <div class="search-option-meta">
                <b class="text-primary">( {{ convertPrice(node.raw.sellingPriceMask) }} )</b>
                <b :class="node.raw.inStockQuantity > 0 ? 'text-success' : 'text-danger'">
                  ( TCB:{{ node.raw.inStockQuantity }} )
                </b>
              </div>
              <div class="search-option-name">
                {{ node.raw.label }}
                <span
                  v-if="node.raw.imeiCode && node.raw.imeiCode.trim().length > 0"
                  class="text-warning"
                >
                  - IMEI: {{ node.raw.imeiCode }}
                </span>
              </div>
            </div>
          </label>
        </treeselect>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import { convertPrice, makeToastFaile } from '@/utils/common';
import { BILL_TYPE, PRODUCT_TYPE } from '@/utils/enum';
import {
  searchByImei,
  searchByImeiForSale,
  searchProductsForSale,
  unwrapProductSearchData,
} from '@/utils/productSearch';
import productSearchAsyncMixin from '@/view/mixins/product-search-async.mixin';

const SEARCH_TYPE = {
  PRODUCT: 1,
  IMEI: 2,
};

export default {
  mixins: [productSearchAsyncMixin],
  components: {
    Treeselect,
  },
  props: {
    storeId: {
      type: Number,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    billType: {
      type: Number,
      default: BILL_TYPE.RETAIL_INVOICE,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    // Cho phép chọn sản phẩm đã hết tồn (vd trả hàng độc lập từ PM cũ — hàng có thể không còn tồn).
    allowOutOfStock: {
      type: Boolean,
      default: false,
    },
    // Trạng thái IMEI cần tìm (vd trả hàng: tìm IMEI ĐÃ BÁN = 2, không tồn trong kho).
    // null => tìm IMEI đang bán được (mặc định, endpoint by-imei-for-sale).
    imeiSearchStatus: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      searchType: SEARCH_TYPE.PRODUCT,
      searchTypeOptions: [
        { id: SEARCH_TYPE.PRODUCT, name: 'Tìm sản phẩm' },
        { id: SEARCH_TYPE.IMEI, name: 'Tìm IMEI' },
      ],
    };
  },
  computed: {
    placeHolder() {
      if (this.searchType === SEARCH_TYPE.IMEI) {
        return 'Tìm kiếm theo mã IMEI';
      }
      return this.compact
        ? 'Tìm tên, mã, mã vạch'
        : 'Tìm kiếm theo tên, mã, mã vạch sản phẩm';
    },
    portalWrapperClass() {
      return this.compact
        ? 'bill-sale-product-search-portal bill-sale-product-search-portal--compact'
        : 'bill-sale-product-search-portal';
    },
  },
  methods: {
    convertPrice,
    getProductPriceByBillType(product) {
      const sellingPrice = product.sellingPrice || 0;
      if (this.billType === BILL_TYPE.WHOLESALE_INVOICE) {
        return product.wholesalePrice || sellingPrice;
      }
      return sellingPrice;
    },
    beforeProductSearchFetch() {
      return Boolean(this.storeId);
    },
    async fetchProductSearchApi(searchQuery, options) {
      const params = {
        searchText: searchQuery,
        storeId: this.storeId,
      };
      let response;
      if (this.searchType === SEARCH_TYPE.IMEI) {
        // Trả hàng: tìm IMEI theo trạng thái chỉ định (vd ĐÃ BÁN) — hàng không còn tồn trong kho.
        response =
          this.imeiSearchStatus != null
            ? await searchByImei(
                { ...params, status: this.imeiSearchStatus },
                options,
              )
            : await searchByImeiForSale(params, options);
      } else {
        response = await searchProductsForSale(params, options);
      }
      return { data: unwrapProductSearchData(response) };
    },
    mapProductSearchOptions(result) {
      const resultData = result?.data || [];
      return resultData.map((element) => {
        const sellingPriceMask = this.getProductPriceByBillType(element);
        return {
          id: element.productId,
          label: element.productName,
          ...element,
          sellingPriceMask,
          inStockQuantity: element.inStockQuantity || 0,
        };
      });
    },
    onSelect(node) {
      if (!node) {
        return;
      }
      if (
        !this.allowOutOfStock &&
        node.productType !== PRODUCT_TYPE.PRODUCT_SERVICE &&
        node.inStockQuantity <= 0
      ) {
        makeToastFaile('Sản phẩm đã hết hàng');
        return;
      }
      this.$emit('select', node);
    },
  },
};
</script>

<style lang="scss" scoped>
.bill-sale-product-search {
  ::v-deep .vue-treeselect__control {
    border-radius: 0.28rem;
    border: 1px solid #ced4da;
    min-height: calc(1.35em + 1.1rem + 2px);
  }
  ::v-deep .vue-treeselect__input-container {
    padding-top: 0;
  }
  ::v-deep .vue-treeselect__placeholder,
  ::v-deep .vue-treeselect__single-value {
    line-height: calc(1.35em + 1.1rem);
  }
}

.search-option-item {
  line-height: 1.35;
}
.search-option-meta {
  margin-bottom: 2px;
  white-space: nowrap;
}
.search-option-name {
  white-space: normal;
  word-break: break-word;
  color: #181c32;
}
</style>

<style lang="scss">
.bill-sale-product-search-portal.vue-treeselect__portal-target {
  width: auto !important;
  min-width: 360px;
}

.bill-sale-product-search-portal--compact.vue-treeselect__portal-target {
  min-width: 480px;
}

.bill-sale-product-search-portal .vue-treeselect__menu {
  min-width: 360px;
  max-width: min(720px, 96vw);
  width: max-content;
}

.bill-sale-product-search-portal--compact .vue-treeselect__menu {
  min-width: 480px;
}

.bill-sale-product-search-portal .vue-treeselect__label {
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
  display: block;
  padding: 0;
  max-width: none;
}

.bill-sale-product-search-portal .vue-treeselect__label-container {
  display: block;
  width: 100%;
  table-layout: auto;
}

.bill-sale-product-search-portal .vue-treeselect__option {
  height: auto;
  min-height: auto;
  padding-top: 6px;
  padding-bottom: 6px;
  line-height: 1.4;
}
</style>
